/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useState} from 'react';
import {DraggableAttributes} from '@dnd-kit/core';
import {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';
import {Grid, GridSize, makeStyles} from '@material-ui/core';
import {DragIndicator} from '@material-ui/icons';
import {translate} from '@ion/core/src/locale/locale';
import {copyToClipBoard} from '@ion/core/src/utils/BrowserUtils';
import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {PushPinIcon} from '@ion/dls/src/icons/PushPinIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';

export interface DeviceDetailsGridItemProps {
  classes?: any;
  children?: JSX.Element;
  mdSpacing?: GridSize;
  label?: string;
  value?: any;
  onClick?: () => void;
  clickTooltip?: string;
  valueToCopyOverride?: any;
  onFavoriteClick?: () => void;
  isFavorited?: boolean;
  isBeingDragged?: boolean;
  doRender?: boolean;
  sortableProps?: SortableProps;
  bodyOverride?: JSX.Element;
}

interface SortableProps {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap;
  setNodeRef: (node: HTMLElement) => void;
  style: React.CSSProperties;
}

const useStyles = makeStyles(() => ({
  formItem : {
    padding : '16px'
  },
  headerItem : {
    color          : dlsColor('grey2'),
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    fontSize       : '14px',
    fontFamily     : 'Roboto',
    fontWeight     : 400,
    lineHeight     : '20px !important',
    marginBottom   : '8px'
  },
  labelText : {
    display     : 'inline-block',
    padding     : '0px',
    margin      : '0px',
    marginRight : '3px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color           : dlsColor('grey1'),
    backgroundColor : dlsColor('white'),
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'flex-start',
    flexWrap        : 'wrap',
    minHeight       : '56px',
    fontSize        : '16px',
    letterSpacing   : 0,
    padding         : '10px 11px',
    overflowWrap    : 'break-word',
    wordWrap        : 'break-word',
    position        : 'relative'
  },
  clickableValue : {
    color          : dlsColor('grey1'),
    cursor         : 'pointer',
    fontWeight     : 500,
    letterSpacing  : '1px',
    textDecoration : 'underline'
  },
  favoriteIcon : {
    margin  : '0px 2px',
    padding : '0px'
  },
  dragIndicator : {
    transform : 'rotate(90deg)',
    margin    : '0px 2px',
    padding   : '0px',
    height    : '20px',
    width     : '20px'
  },
  copyButtonContainer : {
    position : 'absolute',
    right    : '4px'
  },
  disabledButFavoritedInfoIcon : {
    margin     : '0px 2px',
    display    : 'flex',
    alignItems : 'center'
  }
}));

/**
 * A data field component used primarily by the Device Details tab.
 * Has an optional favorite button that appears when hovered over. Also has a
 * copy button that appears when the field is hovered over and a value is given.
 * 
 * ```typescript
 * 
 *   import DeviceDetailsGridItem from './DeviceDetailsGridItem';
 * 
 *   // A basic grid item that doesn't show a favorite button and is not draggable
 *   const gridItem = <DeviceDetailsGridItem label={translate('admin-devices:view.shipping.poNumber')}
 *     value={shippingInfo?.PurchaseOrder} />;
 * 
 *   // A grid item that shows a favorite button
 *   const gridItem = <DeviceDetailsGridItem label={translate('admin-devices:view.shipping.poNumber')}
 *     value={shippingInfo?.PurchaseOrder}
 *     onFavoriteClick : () => handleFavoriteClicked(id),
 *     isFavorited     : checkIsItemFavorited(id),
 *     isBeingDragged  : isDragging />;
 * 
 *   // Passing `sortableProps` allows the field to be dragged and dropped
 *   const gridItem = <DeviceDetailsGridItem {...props} sortableProps={sortableProps} />;
 * 
 * ```
 * 
 * @param label               Title of the field.
 * @param value               Data to show in the field.
 * @param valueToCopyOverride Replaces `value` as the text to copy. Pass this 
 *                            if converting `value` to a string gives an
 *                            incorrect copy value, e.g. if `value` is a
 *                            component. Defaults to `undefined`, so passing
 *                            `null` will still cause this to override `value`.
 * @param mdSpacing           How much horizontal space to take up in the grid
 *                            when the size is medium.
 * @param onFavoriteClick     Callback called when the favorite button is
 *                            clicked. Pass this in if the field should show a
 *                            favorite button when hovered over since it is
 *                            hidden if this prop is not passed in.
 * @param isFavorited         Whether this field has been favorited. Applies to
 *                            both the field in the favorites section and the
 *                            field in the original position.
 * @param isBeingDragged      Whether this field is picked up by the cursor.
 * @param doRender            Whether this field should be rendered.
 * @param sortableProps       Data needed for dragging and dropping. Pass this
 *                            in if this field is currently in a sortable
 *                            context, e.g. in a Favorited Items/Pinned Fields
 *                            section.
 * @param bodyOverride        Component to replace this field's data container
 *                            with. Also replaces the components that render
 *                            `value`, so be sure to render all data if this
 *                            prop is provided.
 */
const DeviceDetailsGridItem = ({
  label = '',
  value = '',
  onClick = undefined,
  clickTooltip = '',
  valueToCopyOverride = undefined,
  mdSpacing = 4,
  onFavoriteClick = null,
  isFavorited = false,
  isBeingDragged = false,
  doRender = true,
  sortableProps = null,
  bodyOverride = null
}: DeviceDetailsGridItemProps) => {
  const classes = useStyles();

  const [isMouseOver, setIsMouseOver]                                     = useState(false);
  const [isFavoriteTooltipOpenOverride, setIsFavoriteTooltipOpenOverride] = useState(false);

  const isValueValid = (val) => {
    return val?.toString().length > 0;
  };

  const closeFavoriteTooltip = () => {
    setIsFavoriteTooltipOpenOverride(true);
  };
  
  useEffect(() => {
    if (isFavoriteTooltipOpenOverride) {
      setIsFavoriteTooltipOpenOverride(false);
    }
  }, [isFavoriteTooltipOpenOverride]);

  const dragIndicatorStyle = useMemo(() => ({
    cursor : isBeingDragged ? 'inherit' : 'grab'
  }), [isBeingDragged]);

  const valueToCopy = useMemo(() => (
    valueToCopyOverride !== undefined ? valueToCopyOverride : value
  ), [valueToCopyOverride, value]);
  
  const handleMouseEnter = function() {
    setIsMouseOver(true);
  };
  const handleMouseLeave = function() {
    setIsMouseOver(false);
  };

  const handleCopy = function() {
    copyToClipBoard(valueToCopy);
  };

  const handleFavoriteClick = function() {
    onFavoriteClick();
    handleMouseLeave();
    closeFavoriteTooltip();
  };

  const favoriteIcon              = <PushPinIcon filled={isFavorited} />;
  const favoriteIconColor         = dlsColor('aaBlue');
  const favoriteButtonTooltipText = isFavorited
    ? translate('admin-devices:view.deviceDetailSummary.unfavoriteTooltipText')
    : translate('admin-devices:view.deviceDetailSummary.favoriteTooltipText');

  const canBeFavorited = onFavoriteClick !== null;
  const isSortable     = sortableProps !== null;

  const doShowFavoriteButton = isBeingDragged ||
    (canBeFavorited && (isSortable ? isMouseOver : isMouseOver || isFavorited));
  const doShowDragIndicator  = (isSortable && isMouseOver) || isBeingDragged;
  const doShowCopyButton     = isMouseOver && isValueValid(valueToCopy) && !isBeingDragged;

  const {
    attributes,
    listeners,
    setNodeRef,
    style
  } = sortableProps ?? {};

  const itemContainerStyle = isBeingDragged ? {...style, cursor: 'grabbing'} : style;

  return (
    <Render.If condition={doRender || isSortable}>
      <Grid className={classes.formItem}
        md={mdSpacing}
        sm={6}
        xs={12}
        item
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={setNodeRef}
        style={itemContainerStyle}
        {...attributes}>

        <div className={classes.headerItem}>
          <p className={classes.labelText}>
            {label}
          </p>
          <Render.If condition={!doRender && isSortable}>
            <Tooltip arrow
              title={translate('admin-devices:view.deviceDetailSummary.disabledButFavoritedInfoTooltipText')}>
              <span className={classes.disabledButFavoritedInfoIcon}>
                <InfoIcon size={IconSize.small} />
              </span>
            </Tooltip>
          </Render.If>
          <Render.If condition={doShowFavoriteButton}>
            <Tooltip title={favoriteButtonTooltipText}
              arrow
              placement={'top'}
              isOpen={isFavoriteTooltipOpenOverride}>
              <IconButton icon={favoriteIcon}
                className={classes.favoriteIcon}
                color={favoriteIconColor}
                onClick={handleFavoriteClick} />
            </Tooltip>
          </Render.If>
          <Render.If condition={doShowDragIndicator}>
            <IconButton icon={<DragIndicator />}
              color='inherit'
              className={classes.dragIndicator}
              style={dragIndicatorStyle}
              {...listeners} />
          </Render.If>
        </div>

        <Render>
          <Render.If condition={bodyOverride === null}>
            <div className={classes.bodyItem}>
              <Render.If condition={Boolean(onClick)}>
                <Tooltip arrow title={clickTooltip}>
                  <span className={classes.clickableValue} onClick={onClick}>
                    {value}
                  </span>
                </Tooltip>
              </Render.If>
              <Render.If condition={!onClick}>
                {value}
              </Render.If>
              <Render.If condition={doShowCopyButton}>
                <div className={classes.copyButtonContainer}>
                  <CopyButton onCopy={handleCopy} size={IconSize.smallMedium} variant='plaintext' />
                </div>
              </Render.If>
            </div>
          </Render.If>
          <Render.Else>
            {bodyOverride}
          </Render.Else>
        </Render>

      </Grid>
    </Render.If>
  );
};

export default DeviceDetailsGridItem;
